import { useEffect, useMemo, useState, useRef } from 'react'
import styled from 'styled-components'
import {
  Card,
  Input,
  InputNumber,
  Button,
  Icon,
  Form,
  Typography,
  Modal,
  notification,
  Select,
} from 'antd'
import SearchableSelect from './ui/SearchableSelect'
import {
  calculateTotalPrice,
  convertUnitPrice,
  extractAndCalculateSize,
  findVenueProductForIngredient,
  getConversionCategory,
  getCustomProductPricePounds,
  mapCustomProductsForSave,
  resolveIngredientUnitPrice,
  resolveVenueProductUnitPrice,
} from 'src/utils/priceFunctions'
import {
  formatCustomIngredientFailureDescription,
  validateCustomProductsForSave,
} from 'src/utils/customIngredientSaveErrors'
import { CloseOutlined } from '@ant-design/icons'
import CustomCategorySearch from './CustomCategorySearch'
import { useDispatch, useSelector } from 'react-redux'
import {
  createCategory,
  setSuccess,
  setError,
  setMonitorCapUpgrade,
} from 'src/features/RecipeBuilderSlice'
import { isAtRecipeMonitorCap } from 'src/constants/recipePlanAllowances'
import { fetchVenueProducts } from 'src/features/VenueProductsSlice'
import RecipeNotes from './ui/RecipeNotes'
import MethodSection from './ui/MethodSection'
import { uploadFile, deleteFile } from 'src/utils/uploadFile'
import PlanLimitModal from 'src/screens/subscriptions/PlanLimitModal'
import { notifyMenuBuilderPaywallSales } from 'src/utils/menuBuilderPaywallNotify'
import CustomProduct from './ui/CustomProduct'
import {
  calculateSellingPriceInclVatFormatted,
  calculateMarginPrice,
  formatMarginPriceForDisplay,
  formatMarginPriceForStorage,
} from 'src/utils/vatCalculations'
import EditIngredientModal from './ui/EditIngredientModal'
import EditCustomIngredientModal from './ui/EditCustomIngredientModal'
import AddRecipeIngredientModal from './AddRecipeIngredientModal'
import ShoppingListGuidanceBanner from './ShoppingListGuidanceBanner'
import ShoppingListEmptyEducation from './ShoppingListEmptyEducation'
import RecipeIngredientRow from './RecipeIngredientRow'
import MenusAndSectionsPanel from './MenusAndSectionsPanel'
import { API } from 'src/utils/interceptor'
import { buildFirstRecipeFormPrefill } from 'src/utils/growthEngineSuggestedProduct'
import {
  canSaveSlimFirstRecipe,
  ensureUncategorisedCategory,
  isSlimFirstRecipeCreate,
} from 'src/utils/growthEngineFirstRecipe'
import { shouldOpenCreateFromRequest } from 'src/utils/growthEngineFirstSuccessKeepHuman'
import {
  RECIPE_GP_PERCENT_HINT,
  RECIPE_GP_PERCENT_LABEL,
  RECIPE_GP_PERCENT_PLACEHOLDER,
  RECIPE_MARGIN_EX_VAT_HINT,
  RECIPE_MARGIN_EX_VAT_LABEL,
  RECIPE_SELLING_PRICE_HINT,
  RECIPE_SELLING_PRICE_LABEL,
  formatRecipeCostPerPortionLabel,
  getRecipeSaveDisabledReason,
  toFiniteRecipeCost,
} from 'src/utils/growthEngineRecipePrices'
import { formatGbp } from 'src/utils/formatGbp'

const { Option } = Select
const { TextArea } = Input
const { Title, Text } = Typography

const StyledCard = styled(Card)`
  .ant-card-head-title {
    font-size: 18px;
  }
`

const IngredientRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  margin-bottom: 16px;
  padding: 16px;
  border-radius: 4px;
  background: #fafafa;

  .ant-form-item {
    margin-bottom: 0;
  }
`

const FileUpload = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;

  img {
    max-width: 700px;
    height: 300px;
    border: 1px solid #ddd;
    border-radius: 5px;
  }

  button {
    background: #b5d922;
    color: #fff;
    width: 100%;
  }
`

const SectionDivider = styled.div`
  margin: 24px 0 16px 0;
  padding: 8px 0;
  border-bottom: 2px solid #f0f0f0;

  h3 {
    margin: 0;
    color: #333;
    font-size: 16px;
    font-weight: bold;
  }
`

const PriceFieldHint = styled.div`
  font-size: 12px;
  color: #666;
  margin: -4px 0 8px 0;
  font-weight: normal;
`

const SaveDisabledReason = styled.div`
  margin-top: 8px;
  color: #cf1322;
  font-size: 13px;
  line-height: 1.4;
`

const calculateGrossProfitPercent = (marginExVat, costPerPortion) => {
  const margin = parseFloat(marginExVat)
  const cost = parseFloat(costPerPortion)
  if (!margin || margin <= 0 || Number.isNaN(cost) || cost < 0) return null
  return Math.round(((margin - cost) / margin) * 10000) / 100
}

const calculateMarginFromGrossProfit = (costPerPortion, gpPercent) => {
  const cost = parseFloat(costPerPortion)
  const gp = parseFloat(gpPercent)
  if (Number.isNaN(cost) || cost < 0 || Number.isNaN(gp) || gp >= 100) {
    return null
  }
  return formatMarginPriceForStorage(cost / (1 - gp / 100))
}

const conversionOptions = {
  weight: [
    { value: 'kg', label: 'Kilogram (kg)' },
    { value: 'g', label: 'Gram (g)' },
    { value: 'mg', label: 'Milligram (mg)' },
    { value: 'lb', label: 'Pound (lb)' },
    { value: 'oz', label: 'Ounce (oz)' },
  ],
  volume: [
    { value: 'ltr', label: 'Liter (ltr)' },
    { value: 'ml', label: 'Milliliter (ml)' },
    { value: 'cl', label: 'Centiliter (cl)' },
    { value: 'gal', label: 'Gallon (gal)' },
    { value: 'floz', label: 'Fluid Ounce (fl oz)' },
  ],
  count: [
    { value: 'unit', label: 'Unit' },
    { value: 'piece', label: 'Piece' },
    { value: 'item', label: 'Item' },
    { value: 'pack', label: 'Pack' },
    { value: 'box', label: 'Box' },
  ],
}

const resolveConversionObject = (conversion, conversions = []) => {
  if (!conversion) return null
  if (
    typeof conversion === 'object' &&
    (conversion.toUnit != null || conversion.toQty != null)
  ) {
    return conversion
  }

  const conversionId =
    typeof conversion === 'string' || typeof conversion === 'number'
      ? String(conversion)
      : String(
          conversion?._id || conversion?.conversionId || conversion?.id || '',
        )

  if (!conversionId) {
    return typeof conversion === 'object' ? conversion : null
  }

  return (
    conversions.find(
      candidate =>
        String(candidate?._id || '') === conversionId ||
        String(candidate?.conversionId || '') === conversionId ||
        String(candidate?.id || '') === conversionId,
    ) || (typeof conversion === 'object' ? conversion : null)
  )
}

const normalizeRecipeData = (data, venueProducts = []) => {
  // Debug: Check custom ingredients in raw data
  if (data.customIngredients && data.customIngredients.length > 0) {
    void 0
  }

  const normalizedIngredients = (data.ingredients || []).map(item => {
    const productRef = item.product
    const product =
      productRef && typeof productRef === 'object' ? productRef : {}
    const productIdAsString =
      typeof productRef === 'string' || typeof productRef === 'number'
        ? String(productRef)
        : ''
    const extractSize = extractAndCalculateSize(product?.size || '')
    const selectedSize = item.size || extractSize.numbers?.[0] || 0
    const venueProduct = findVenueProductForIngredient(
      {
        ...item,
        productId:
          item.productId || productIdAsString || product?.venueProductId,
        venueProductId:
          item.venueProductId || productIdAsString || product?.venueProductId,
      },
      venueProducts,
    )
    const catalogProduct = venueProduct?.product || product
    const price = resolveIngredientUnitPrice({
      ingredient: item,
      venueProduct,
      product: catalogProduct,
    })

    const conversionCandidates = [
      ...(Array.isArray(item?.conversions) ? item.conversions : []),
      ...(Array.isArray(product?.conversions) ? product.conversions : []),
      ...(Array.isArray(venueProduct?.conversions)
        ? venueProduct.conversions
        : []),
      ...(Array.isArray(catalogProduct?.conversions)
        ? catalogProduct.conversions
        : []),
    ]
    const resolvedConversion = resolveConversionObject(
      item.conversion,
      conversionCandidates,
    )

    let totalPrice = 0
    if (
      resolvedConversion?.toUnit &&
      resolvedConversion?.toQty != null &&
      item?.UoM
    ) {
      const converted = convertUnitPrice({
        actualUnit: resolvedConversion.toUnit,
        actualPrice: price,
        actualFactor: resolvedConversion.toQty,
        newUnit: item.UoM,
        newFactor: item?.size,
      })
      totalPrice =
        converted != null && Number.isFinite(Number(converted))
          ? converted
          : calculateTotalPrice(
              catalogProduct.size || product.size,
              price,
              selectedSize,
            ) || 0
    } else {
      totalPrice =
        calculateTotalPrice(
          catalogProduct.size || product.size,
          price,
          selectedSize,
        ) || 0
    }

    return {
      venueProductId:
        product.venueProductId || productIdAsString || venueProduct?._id || '',
      productId:
        product.venueProductId || productIdAsString || venueProduct?._id || '',
      _id: product._id || catalogProduct?._id || '',
      sku: product.sku || catalogProduct?.sku || item.sku || '',
      title:
        item.title ||
        product.title ||
        venueProduct?.title ||
        catalogProduct?.title ||
        '',
      size: selectedSize,
      price,
      UoM: item.UoM || product.UoM || catalogProduct?.UoM || '',
      totalPrice: totalPrice || 0,
      conversions:
        conversionCandidates.length > 0
          ? conversionCandidates
          : product?.conversions ?? venueProduct?.conversions ?? [],
      conversion: resolvedConversion,
      defaultUnit: product.UoM || catalogProduct?.UoM,
      defaultSize: product.size || catalogProduct?.size,
    }
  })

  // Normalize recipe ingredients
  const normalizedRecipeIngredients = (data.recipeIngredients || []).map(
    item => ({
      id: item.id || `recipe-${Date.now()}-${Math.random()}`,
      recipeId: item.recipeId,
      recipeName: item.recipeName,
      portions: item.portions || 1,
      isLocked: item.isLocked !== undefined ? item.isLocked : true,
      costPerPortion: item.costPerPortion || 0,
      totalCost:
        item.totalCost || (item.costPerPortion || 0) * (item.portions || 1),
      allergens: item.allergens || [],
      baseRecipe: item.baseRecipe || null,
    }),
  )

  const categoryId =
    (typeof data.category === 'object' && data.category?._id) ||
    (typeof data.category === 'string' ? data.category : '') ||
    ''

  const normalizedData = {
    _id: data._id,
    title: data.title || '',
    description: data.description || '',
    portions: data.portions || 1,
    sellingPrice: data.sellingPrice
      ? formatMarginPriceForStorage(data.sellingPrice)
      : '',
    hasVat: data.hasVat !== undefined ? data.hasVat : false,
    totalIngredientsPrice: '',
    costPerPortion: data.costPerPortion || '',
    costPerRecipe: '',
    grossProfit: data.grossProfit || '',
    category: categoryId,
    servingSize: data.servingSize || 400,
    image: data.image || null,
    ingredients: normalizedIngredients,
    recipeIngredients: normalizedRecipeIngredients, // Add recipe ingredients
    notes: {
      ...data.notes,
    },
    steps: data.steps?.length ? data.steps : [''],
    customProducts: (data.customIngredients || data.customProducts || []).map(
      item => ({
        ...item,
        pricePounds: getCustomProductPricePounds(item),
      }),
    ),
    menuInfo: data.menuInfo || [], // Include menuInfo for MenusAndSectionsPanel
  }

  // Debug: Check custom ingredients after normalization
  if (
    normalizedData.customProducts &&
    normalizedData.customProducts.length > 0
  ) {
    void 0
  }

  return normalizedData
}

const initialState = {
  title: '',
  description: '',
  portions: 1,
  sellingPrice: '',
  hasVat: false,
  totalIngredientsPrice: '',
  costPerPortion: '',
  costPerRecipe: '',
  grossProfit: '',
  category: '',
  servingSize: 400,
  image: null,
  ingredients: [
    {
      sku: '',
      title: '',
      size: '',
      price: '',
      UoM: '',
      totalPrice: '',
    },
  ],
  recipeIngredients: [], // Add recipe ingredients to initial state
  notes: {
    text: '',
    images: [],
  },
  steps: [''],
  customProducts: [],
  menuInfo: [], // Add menuInfo to initial state
}

const RecipeManager = ({
  products,
  categories,
  recipes = [], // Add recipes prop for recipe stacking
  onSaveRecipe,
  update,
  recipe,
  setUpdate = () => {},
  onAllergenUpdate,
  suggestedProduct = null,
  requestOpenCreate = 0,
  onCreateModalOpenChange,
  allowCreate = true,
}) => {
  const { isLoading, error, success } = useSelector(state => state.recipes)
  const venueId = useSelector(state => state.venue?.venue?._id)
  const dispatch = useDispatch()
  const fileInputRef = useRef(null)
  const [visible, open] = useState(false)
  const hydratedEditRecipeIdRef = useRef(null)
  const appliedSuggestedProductRef = useRef(false)
  const lastOpenedCreateRequestRef = useRef(0)
  const [openCategoryDropdown, setOpenCategoryDropdown] = useState(false)
  const [formState, setFormState] = useState(initialState)
  const isSlimFirstRecipe = isSlimFirstRecipeCreate({ update, recipes })

  // Filter out custom products from the products array for SearchableSelect
  // Custom products have a different structure and should not be used in SearchableSelect
  const filteredProducts = useMemo(() => {
    return (
      products?.filter(product => {
        // Regular products have a 'product' property, custom products don't
        // This prevents the "Item with missing product" warnings for custom products
        return product && product.product && typeof product.product === 'object'
      }) || []
    )
  }, [products])
  const [showEditIngredient, setShowEditIngredient] = useState(false)
  const [selectedIngredient, setSelectedIngredient] = useState({})
  const [showAddRecipeModal, setShowAddRecipeModal] = useState(false)
  const [showEditCustomIngredient, setShowEditCustomIngredient] =
    useState(false)
  // Temporary state while editing margin (2dp display) or selling (inc VAT)
  const [tempMarginDisplay, setTempMarginDisplay] = useState('')
  const [isEditingMargin, setIsEditingMargin] = useState(false)
  const [tempSellingPriceInclVat, setTempSellingPriceInclVat] = useState('')
  const [isEditingSellingPrice, setIsEditingSellingPrice] = useState(false)
  const lastPricingEditRef = useRef(null)
  const editingSourceRef = useRef(null)
  const [editingCustomIngredient, setEditingCustomIngredient] = useState({})
  const [editingCustomIngredientIndex, setEditingCustomIngredientIndex] =
    useState(null)

  const {
    sellingPrice,
    hasVat,
    costPerRecipe,
    costPerPortion,
    portions,
    grossProfit,
    customProducts,
  } = formState
  const [isUploading, setIsUploading] = useState(false)
  const [localImageUrl, setLocalImageUrl] = useState(null)
  const [uploadingIndexes, setUploadingIndexes] = useState([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const { currentPlan } =
    useSelector(state => state.venue?.venue?.usageLimits) ?? {}
  const recipeAllowance = useSelector(state => state.recipes?.recipeAllowance)
  const monitorCapUpgrade = useSelector(
    state => state.recipes?.monitorCapUpgrade,
  )
  const [showLimitModal, setShowLimitModal] = useState(false)

  const openRecipeCreatePaywall = () => {
    notifyMenuBuilderPaywallSales({
      venueId,
      source: 'client',
      platform: 'dashboard',
    })
    setShowLimitModal(true)
  }

  useEffect(() => {
    if (!monitorCapUpgrade) return
    openRecipeCreatePaywall()
    dispatch(setMonitorCapUpgrade(false))
  }, [monitorCapUpgrade])

  // const getuniqueSize = useMemo(() => {
  //   const uniqueSizes = products.reduce((acc, venueProduct) => {
  //     const { product } = venueProduct ?? {}
  //     if (isNaN(product.size) && !acc.includes(product.size)) {
  //       acc.push({ size: product.size, price: product.price, UoM: product.UoM })
  //     }
  //     return acc
  //   }, [])
  //   return uniqueSizes
  // }, [products])

  // Filter available recipes (exclude current recipe to prevent self-reference)
  const availableRecipes = useMemo(() => {
    return recipes.filter(r => r._id !== formState._id)
  }, [recipes, formState._id])

  // Check for missing products in current recipe
  const missingProducts = useMemo(() => {
    const missing = []

    // Only check for missing products if this is an existing recipe (update mode)
    // and there are actual ingredients with data
    if (update && formState.ingredients) {
      formState.ingredients.forEach((ingredient, index) => {
        // Check if ingredient has a valid title either directly or through product
        const hasTitle = Boolean(
          ingredient.title && ingredient.title.trim() !== '',
        )
        // const hasProduct = ingredient.product && ingredient.product._id
        const hasProductId =
          ingredient.productId || ingredient._id || ingredient.venueProductId

        // Only flag as missing if there's some data but no valid title or product
        const hasSomeData =
          ingredient.sku ||
          ingredient.title ||
          ingredient.product ||
          ingredient.productId
        // const isIncomplete = hasSomeData && (!hasTitle || !hasProductId)

        // Special case: if ingredient has productId but no title, it might still be valid
        // if it has other product data (this handles the case where product is selected but title is empty)
        const hasValidProductData =
          hasProductId && (hasTitle || ingredient.sku || ingredient.price)
        const isActuallyIncomplete = hasSomeData && !hasValidProductData

        // Additional check: if ingredient has a productId but no title, and no other product data,
        // it might be a valid ingredient that just needs a title lookup
        const needsTitleLookup =
          hasProductId && !hasTitle && !ingredient.sku && !ingredient.price
        const isActuallyIncompleteFinal =
          isActuallyIncomplete && !needsTitleLookup

        // Debug logging
        if (hasSomeData) {
          void 0
        }

        if (isActuallyIncompleteFinal) {
          missing.push({
            index,
            ingredient,
            reason: !hasProductId
              ? 'Product not found'
              : 'Missing product title',
          })
        }
      })
    }
    return missing
  }, [formState.ingredients, update])

  // Check if recipe has any missing products
  const hasMissingProducts = missingProducts.length > 0

  // Debug logging for missing products changes
  useEffect(() => {
    // })
  }, [hasMissingProducts, missingProducts])

  // Function to add missing products to shopping list
  const handleAddMissingProducts = async () => {
    try {
      const response = await API.post(
        'api2',
        `venue/${venueId}/add-missing-products`,
        {
          body: { missingProducts },
        },
      )

      if (response.success) {
        notification.success({
          message: 'Products Added Successfully',
          description: `Added ${
            response.data.totalAdded
          } products to your shopping list. ${
            response.data.totalErrors > 0
              ? `${response.data.totalErrors} products could not be added.`
              : ''
          }`,
          duration: 5,
        })

        // Refresh the products list to show the newly added products
        await dispatch(fetchVenueProducts(venueId))

        // Also refresh the products prop that's passed to this component
        // This will help update the missing products detection
        if (onAllergenUpdate) {
          onAllergenUpdate()
        }
      } else {
        notification.error({
          message: 'Failed to Add Products',
          description:
            response.message ||
            'An error occurred while adding products to your shopping list.',
          duration: 5,
        })
      }
    } catch (error) {
      console.error('Error adding missing products:', error)
      notification.error({
        message: 'Failed to Add Products',
        description:
          'An error occurred while adding products to your shopping list. Please try again.',
        duration: 5,
      })
    }
  }

  // Helper: compute allergens for a recipe including stacked recipe ingredients
  const computeRecipeAllergens = useMemo(() => {
    const buildAllergens = (baseRecipe, visited = new Set()) => {
      if (!baseRecipe || visited.has(baseRecipe._id)) return []
      visited.add(baseRecipe._id)

      const allergenSet = new Set()

      ;(baseRecipe.ingredients || []).forEach(ing => {
        // Check both normalized structure and original structure
        const allergens = ing.allergens || ing?.product?.allergens || []
        const allergenTraces =
          ing.allergenTraces || ing?.product?.allergenTraces || []

        allergens.forEach(a => allergenSet.add(a))
        allergenTraces.forEach(a => allergenSet.add(a))
      })
      ;(baseRecipe.customIngredients || []).forEach(ing => {
        // Check both normalized structure and original structure
        const allergens = ing.allergens || ing?.product?.allergens || []
        const allergenTraces =
          ing.allergenTraces || ing?.product?.allergenTraces || []

        allergens.forEach(a => allergenSet.add(a))
        allergenTraces.forEach(a => allergenSet.add(a))
      })
      ;(baseRecipe.recipeIngredients || []).forEach(ri => {
        const child = availableRecipes.find(r => r._id === ri.recipeId)
        if (child) {
          buildAllergens(child, visited).forEach(a => allergenSet.add(a))
        }
      })

      return Array.from(allergenSet)
    }

    return buildAllergens
  }, [availableRecipes])

  const handleChange = (key, value) => {
    setFormState(prev => ({
      ...prev,
      [key]: typeof value === 'function' ? value(prev[key]) : value,
    }))
  }

  const updatePricingFromMargin = (marginValue, source = 'margin') => {
    const stored = formatMarginPriceForStorage(marginValue)
    if (!stored) {
      setFormState(prev => ({ ...prev, sellingPrice: '', grossProfit: '' }))
      return
    }
    lastPricingEditRef.current = source
    const gp = calculateGrossProfitPercent(stored, costPerPortion)
    setFormState(prev => ({
      ...prev,
      sellingPrice: stored,
      grossProfit: gp !== null ? gp : prev.grossProfit,
    }))
  }

  const handleGpChange = value => {
    if (value === null || value === undefined || value === '') {
      handleChange('grossProfit', '')
      return
    }
    if (value >= 100) {
      notification.warning({
        message: 'Invalid gross profit',
        description: 'Gross profit must be less than 100%.',
        duration: 3,
      })
      return
    }
    if (!costPerPortion) {
      handleChange('grossProfit', Number(Number(value).toFixed(2)))
      return
    }
    lastPricingEditRef.current = 'gp'
    editingSourceRef.current = 'gp'
    const margin = calculateMarginFromGrossProfit(costPerPortion, value)
    setFormState(prev => ({
      ...prev,
      grossProfit: Number(Number(value).toFixed(2)),
      sellingPrice: margin || prev.sellingPrice,
    }))
    editingSourceRef.current = null
  }

  // Validate price input
  const validatePrice = value => {
    if (value === '') return true
    const num = parseFloat(value)
    return !isNaN(num) && num >= 0
  }

  useEffect(() => {
    if (!update || !recipe || success) {
      return
    }

    const recipeId = String(recipe?._id || '')
    if (!recipeId) {
      return
    }

    if (hydratedEditRecipeIdRef.current === recipeId) {
      return
    }

    const normalizedData = normalizeRecipeData(recipe, filteredProducts)
    hydratedEditRecipeIdRef.current = recipeId
    setFormState(normalizedData)
    open(true)
  }, [recipe, update, success, filteredProducts])

  useEffect(() => {
    if (update || !allowCreate) return
    const prefill = buildFirstRecipeFormPrefill(suggestedProduct, products)
    if (!prefill) return
    const hasRecipes = Array.isArray(recipes) && recipes.length > 0
    if (
      hasRecipes &&
      !shouldOpenCreateFromRequest({
        requestOpenCreate,
        lastOpenedRequest: lastOpenedCreateRequestRef.current,
      })
    ) {
      return
    }
    const key = `${suggestedProduct?.productId || ''}:${
      suggestedProduct?.title || ''
    }:${requestOpenCreate}`
    if (appliedSuggestedProductRef.current === key) return
    appliedSuggestedProductRef.current = key
    lastOpenedCreateRequestRef.current = requestOpenCreate
    setFormState(prev => ({
      ...prev,
      title: prev.title || prefill.title,
      ingredients: prefill.ingredients,
    }))
    open(true)
  }, [
    suggestedProduct,
    products,
    update,
    requestOpenCreate,
    allowCreate,
    recipes,
  ])

  useEffect(() => {
    if (update || !requestOpenCreate || !allowCreate) return
    if (
      !shouldOpenCreateFromRequest({
        requestOpenCreate,
        lastOpenedRequest: lastOpenedCreateRequestRef.current,
      })
    ) {
      return
    }
    // Prefill path opens in the effect above.
    if (buildFirstRecipeFormPrefill(suggestedProduct, products)) return
    const progressCreate =
      Boolean(suggestedProduct?.fromProgress) &&
      Boolean(suggestedProduct?.openCreate)
    // Progress create=1 on an empty venue: open Create New Recipe once allowed.
    if (
      !progressCreate &&
      (!Array.isArray(products) || products.length === 0)
    ) {
      return
    }
    lastOpenedCreateRequestRef.current = requestOpenCreate
    open(true)
  }, [requestOpenCreate, suggestedProduct, products, update, allowCreate])

  useEffect(() => {
    onCreateModalOpenChange?.(Boolean(visible && !update))
  }, [visible, update, onCreateModalOpenChange])

  useEffect(() => {
    if (!update) {
      hydratedEditRecipeIdRef.current = null
    }
  }, [update])

  // Auto-update locked recipe ingredients when base recipes change (cost/allergens)
  useEffect(() => {
    if (!formState.recipeIngredients?.length) return

    const updated = formState.recipeIngredients.map(ri => {
      if (!ri.isLocked) return ri
      const base = availableRecipes.find(r => r._id === ri.recipeId)
      if (!base) return ri
      const nextCostPerPortion = Number(base.costPerPortion || 0)
      const nextTotal = Number(
        (nextCostPerPortion * (ri.portions || 1)).toFixed(6),
      )
      const nextAllergens = computeRecipeAllergens(base)

      const changed =
        nextCostPerPortion !== ri.costPerPortion ||
        nextTotal !== ri.totalCost ||
        (Array.isArray(ri.allergens)
          ? ri.allergens.join('|') !== nextAllergens.join('|')
          : nextAllergens.length > 0)

      if (!changed) return ri
      return {
        ...ri,
        costPerPortion: nextCostPerPortion,
        totalCost: nextTotal,
        allergens: nextAllergens,
        baseRecipe: base,
        wasAutoUpdated: true,
      }
    })

    // Only set state if something actually changed
    const diff = updated.some((ri, i) => {
      const prev = formState.recipeIngredients[i]
      return (
        ri.costPerPortion !== prev.costPerPortion ||
        ri.totalCost !== prev.totalCost ||
        (ri.allergens || []).join('|') !== (prev.allergens || []).join('|')
      )
    })

    if (diff) {
      setFormState(prev => ({ ...prev, recipeIngredients: updated }))
    }
  }, [availableRecipes, formState.recipeIngredients, computeRecipeAllergens])

  const handleIngredientChange = (index, key, value) => {
    const updatedIngredients = [...formState.ingredients]

    if (key === 'productId') {
      const foundProduct = products.find(p => p._id === value)

      if (!foundProduct) {
        console.error('Product not found for ID:', value)
        return
      }

      const { product, _id, conversions, title } = foundProduct
      let extractSize = extractAndCalculateSize(product?.size || '')
      const unitPrice = resolveVenueProductUnitPrice(foundProduct)

      const newIngredient = {
        ...updatedIngredients[index],
        ...product,
        productId: _id,
        sku: product.sku || '',
        title: title || product.title || '',
        size: extractSize.numbers[0] || 0,
        conversions: conversions ?? [],
        conversion: null,
        defaultSize: product.size,
        UoM: product.UoM || '',
        defaultUnit: product.UoM,
        price: unitPrice,
        totalPrice:
          calculateTotalPrice(
            product.size,
            unitPrice,
            extractSize.numbers[0],
          ) || '',
      }

      updatedIngredients[index] = newIngredient
    } else if (key === 'conversionQuantity') {
      updatedIngredients[index].size = value
      const { toUnit, toQty } = updatedIngredients[index].conversion || {}
      const { price, UoM } = updatedIngredients[index]

      if (toUnit && toQty && price) {
        const converted = convertUnitPrice({
          actualUnit: toUnit,
          actualPrice: price,
          actualFactor: toQty,
          newUnit: UoM,
          newFactor: value,
        })
        if (converted != null) {
          updatedIngredients[index].totalPrice = converted
        }
      }
    } else if (key === 'conversionUnit') {
      updatedIngredients[index].UoM = value
      const { toQty, toUnit } = updatedIngredients[index].conversion || {}
      const { price, size } = updatedIngredients[index]

      if (toUnit && toQty && price && size) {
        const converted = convertUnitPrice({
          actualUnit: toUnit,
          actualPrice: price,
          actualFactor: toQty,
          newUnit: value,
          newFactor: size,
        })
        if (converted != null) {
          updatedIngredients[index].totalPrice = converted
        }
      }
    } else {
      updatedIngredients[index] = { ...updatedIngredients[index], [key]: value }
      if (key === 'size' && updatedIngredients[index].productId) {
        const product = products.find(
          p => p._id === updatedIngredients[index].productId,
        ).product
        const totalPrice = calculateTotalPrice(
          product.size,
          updatedIngredients[index].price,
          updatedIngredients[index].size,
        )
        updatedIngredients[index] = { ...updatedIngredients[index], totalPrice }
      }
    }
    setFormState(prev => ({ ...prev, ingredients: updatedIngredients }))
  }

  // Handle recipe ingredient operations
  const handleAddRecipeIngredient = recipeIngredient => {
    setFormState(prev => ({
      ...prev,
      recipeIngredients: [...prev.recipeIngredients, recipeIngredient],
    }))

    notification.success({
      message: 'Recipe Added',
      description: `Added ${recipeIngredient.recipeName} (${
        recipeIngredient.portions
      } portion${recipeIngredient.portions !== 1 ? 's' : ''})`,
    })
  }

  const handleUpdateRecipeIngredient = updatedRecipeIngredient => {
    setFormState(prev => ({
      ...prev,
      recipeIngredients: prev.recipeIngredients.map(ri =>
        ri.id === updatedRecipeIngredient.id ? updatedRecipeIngredient : ri,
      ),
    }))
  }

  const handleRemoveRecipeIngredient = recipeIngredientId => {
    setFormState(prev => ({
      ...prev,
      recipeIngredients: prev.recipeIngredients.filter(
        ri => ri.id !== recipeIngredientId,
      ),
    }))
  }

  const handleToggleRecipeLock = updatedRecipeIngredient => {
    setFormState(prev => ({
      ...prev,
      recipeIngredients: prev.recipeIngredients.map(ri =>
        ri.id === updatedRecipeIngredient.id ? updatedRecipeIngredient : ri,
      ),
    }))

    notification.info({
      message: 'Recipe Lock Updated',
      description: `Recipe ${
        updatedRecipeIngredient.isLocked ? 'locked' : 'unlocked'
      } successfully`,
    })
  }

  const handleSubmit = async e => {
    e.preventDefault()

    let categoryId = formState.category
    if (isSlimFirstRecipe) {
      if (
        !canSaveSlimFirstRecipe({
          title: formState.title,
          ingredients: formState.ingredients,
          sellingPrice: formState.sellingPrice,
        })
      ) {
        return
      }
      try {
        const category = await ensureUncategorisedCategory({
          categories,
          createCategory: name => dispatch(createCategory(venueId, name)),
        })
        categoryId = category?._id
      } catch (error) {
        notification.error({
          message: 'Could not create Uncategorised',
          description:
            error?.message ||
            'Try again. You need a category to save the first dish.',
        })
        return
      }
      if (!categoryId) {
        notification.error({
          message: 'Could not create Uncategorised',
          description: 'Try again. You need a category to save the first dish.',
        })
        return
      }
    } else if (!formState.category) {
      notification.error({
        message: 'Category Required',
        description: 'Please select a category first.',
      })
      setOpenCategoryDropdown(true)
      return
    }

    const validIngredients = formState.ingredients
      .filter(ing => {
        const hasValidId =
          ing && (ing.productId || ing._id || ing.venueProductId)
        return hasValidId
      })
      .map(item => {
        const mappedItem = {
          product: item?._id || item?.productId || item?.venueProductId,
          title: item.title || '', // Preserve the ingredient title
          sku: item.sku || '', // Preserve the SKU
          size: item.size,
          UoM: item.UoM,
          conversion: item?.conversion?._id,
        }
        return mappedItem
      })

    const validRecipeIngredients = formState.recipeIngredients.map(item => ({
      recipeId: item.recipeId,
      portions: item.portions,
      isLocked: item.isLocked,
      costPerPortion: item.costPerPortion,
      totalCost: item.totalCost,
    }))

    if (
      !validIngredients.length &&
      !validRecipeIngredients.length &&
      !customProducts.length
    ) {
      notification.error({
        message: 'No Ingredients',
        description: 'Please select at least one ingredient or recipe.',
      })
      return
    }

    const validSteps = formState?.steps?.filter(step => step != '')
    const mappedCustomProducts = isSlimFirstRecipe
      ? []
      : mapCustomProductsForSave(formState.customProducts)
    if (!isSlimFirstRecipe) {
      const customValidation =
        validateCustomProductsForSave(mappedCustomProducts)

      if (!customValidation.valid) {
        notification.error({
          message: 'Custom ingredients need attention',
          description: formatCustomIngredientFailureDescription({
            details: { failures: customValidation.failures },
          }),
          duration: 8,
        })
        return
      }
    }

    const imageRemoved = !formState.image
    const savePayload = {
      ...formState,
      category: categoryId,
      sellingPrice:
        formatMarginPriceForStorage(formState.sellingPrice) ||
        formState.sellingPrice,
      costPerPortion: toFiniteRecipeCost(formState.costPerPortion, 0),
      customProducts: isSlimFirstRecipe ? [] : mappedCustomProducts,
      ingredients: validIngredients,
      recipeIngredients: isSlimFirstRecipe ? [] : validRecipeIngredients,
      steps: isSlimFirstRecipe ? [''] : validSteps,
      venueId: venueId,
      image: isSlimFirstRecipe || imageRemoved ? null : formState.image,
      notes: isSlimFirstRecipe ? { text: '', images: [] } : formState.notes,
      menuInfo: isSlimFirstRecipe ? [] : formState.menuInfo,
      ...(imageRemoved && update ? { removeImage: true } : {}),
    }

    try {
      onSaveRecipe(savePayload)
    } catch (error) {
      console.error('Error in onSaveRecipe:', error)
      notification.error({
        message: 'Save Error',
        description: `Failed to save recipe: ${
          error.message || 'Unknown error'
        }`,
        duration: 5,
      })
    }
  }

  useEffect(() => {
    if (!isLoading && !error && success) {
      open(false)
      setUpdate(false)
      setFormState(initialState)
      setLocalImageUrl(null)
      setOpenCategoryDropdown(false)
      dispatch(setSuccess(false))
    }
  }, [isLoading, error, success, venueId])

  // Keep the editor open on save errors so ingredients are not lost.
  useEffect(() => {
    if (!isLoading && error) {
      dispatch(setError(null))
      dispatch(setSuccess(false))
    }
  }, [isLoading, error, dispatch])

  // When cost per portion changes, update margin or GP depending on last user edit
  useEffect(() => {
    if (!costPerPortion || editingSourceRef.current) return

    if (
      lastPricingEditRef.current === 'gp' &&
      grossProfit !== '' &&
      grossProfit !== null &&
      grossProfit !== undefined
    ) {
      const margin = calculateMarginFromGrossProfit(costPerPortion, grossProfit)
      if (margin) {
        setFormState(prev => ({ ...prev, sellingPrice: margin }))
      }
      return
    }

    if (sellingPrice) {
      const gp = calculateGrossProfitPercent(sellingPrice, costPerPortion)
      if (gp !== null) {
        setFormState(prev => ({ ...prev, grossProfit: gp }))
      }
    } else if (!sellingPrice) {
      setFormState(prev => ({ ...prev, grossProfit: '' }))
    }
  }, [costPerPortion])

  // Use temporary state while editing, otherwise calculate from margin price
  const sellingPriceInclVat = isEditingSellingPrice
    ? tempSellingPriceInclVat
    : calculateSellingPriceInclVatFormatted(sellingPrice, hasVat)

  const saveDisabledReason = getRecipeSaveDisabledReason({
    title: formState.title,
    ingredients: formState.ingredients,
    customProducts: formState.customProducts,
    recipeIngredients: formState.recipeIngredients,
    sellingPrice: formState.sellingPrice,
    isSlimFirstRecipe,
  })
  const saveDisabled =
    hasMissingProducts ||
    isUploading ||
    uploadingIndexes.some(uploading => uploading) ||
    (isSlimFirstRecipe
      ? Boolean(saveDisabledReason)
      : Boolean(saveDisabledReason) || !sellingPrice || !costPerRecipe)

  useEffect(() => {
    if (costPerRecipe && portions) {
      const portionPrice = costPerRecipe / portions
      handleChange('costPerPortion', Number(portionPrice.toFixed(2)))
    }
  }, [costPerRecipe, portions])

  useEffect(() => {
    if (
      formState.ingredients.length ||
      formState.customProducts.length ||
      formState.recipeIngredients.length
    ) {
      const ingredientsTotal = formState.ingredients.reduce(
        (acc, ing) => acc + (Number(ing.totalPrice) || 0),
        0,
      )
      const customProductsTotal = formState.customProducts.reduce(
        (acc, product) => {
          const unitPrice = getCustomProductPricePounds(product)
          const size = Number(product.size) || 0
          if (unitPrice > 0 && size > 0) {
            return acc + unitPrice * size
          }
          return acc + (Number(product?.totalPrice) || 0)
        },
        0,
      )
      const recipeIngredientsTotal = formState.recipeIngredients.reduce(
        (acc, recipe) => acc + (Number(recipe.totalCost) || 0),
        0,
      )
      handleChange(
        'costPerRecipe',
        ingredientsTotal + customProductsTotal + recipeIngredientsTotal,
      )
    }
  }, [
    formState.ingredients,
    formState.customProducts,
    formState.recipeIngredients,
  ])

  const handleFileChange = async e => {
    const selectedFile = e.target.files[0]
    if (!selectedFile) return

    const localUrl = URL.createObjectURL(selectedFile)
    setLocalImageUrl(localUrl)

    setIsUploading(true)

    try {
      const uploadedData = await uploadFile(selectedFile)
      if (uploadedData) {
        handleChange('image', uploadedData)
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      notification.error({
        message: 'Upload Failed',
        description:
          'There was an error uploading the image. Please try again.',
      })
    } finally {
      setIsUploading(false)
    }
  }

  const handleDeleteImage = async () => {
    const imageKey = formState.image?.keyName || recipe?.keyName

    handleChange('image', null)
    setLocalImageUrl(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }

    if (!imageKey) {
      return
    }

    const success = await deleteFile(imageKey)
    if (!success) {
      notification.warning({
        message: 'Image removed',
        description:
          'The image was removed from this recipe. We could not delete the file from storage; save the recipe to finish clearing it.',
        duration: 5,
      })
    }
  }

  const handleClose = () => {
    setOpenCategoryDropdown(false)
    open(false)
    setUpdate(false)
    setFormState(initialState)
  }

  const handleChangeCustomProduct = (index, field, value, nested = false) => {
    setFormState(prev => {
      const updatedProducts = [...prev.customProducts]
      const item = { ...updatedProducts[index] }

      if (nested) {
        if (field === 'size') {
          item.size = value
          item.product = { ...item.product, size: value }
        } else if (field === 'price') {
          item.pricePounds = value
        }
        const unitPrice = getCustomProductPricePounds(item)
        item.totalPrice = (unitPrice * item.size).toFixed(3)
      } else {
        item[field] = value
        if (field === 'price') {
          item.pricePounds = value
        }
        const unitPrice = getCustomProductPricePounds(item)
        item.totalPrice = (unitPrice * item.size).toFixed(3)
      }

      updatedProducts[index] = item

      return {
        ...prev,
        customProducts: updatedProducts,
      }
    })
  }

  return (
    <div>
      {allowCreate ? (
        <Button
          type='primary'
          onClick={() => {
            open(true)
            if (isAtRecipeMonitorCap(recipeAllowance)) {
              openRecipeCreatePaywall()
            }
          }}
        >
          Create New Recipe
        </Button>
      ) : null}
      {showLimitModal && (
        <PlanLimitModal
          showLimitModal={showLimitModal}
          setShowLimitModal={setShowLimitModal}
          variant='recipeCreateLimit'
          plan={currentPlan}
        />
      )}
      <Modal
        visible={visible}
        title={update ? 'Update Recipe' : 'Create New Recipe'}
        onCancel={handleClose}
        onClick={() => void 0}
        footer={null}
        width={1300}
        maskClosable={false}
      >
        <CustomProduct
          isModalVisible={isModalVisible}
          setIsModalVisible={setIsModalVisible}
          handleChange={handleChange}
        />
        <EditIngredientModal
          showEditIngredient={showEditIngredient}
          setShowEditIngredient={setShowEditIngredient}
          ingredient={selectedIngredient}
          formState={formState}
          setFormState={setFormState}
          index={selectedIngredient.index}
          onAllergenUpdate={onAllergenUpdate}
        />
        <EditCustomIngredientModal
          showEditCustomIngredient={showEditCustomIngredient}
          setShowEditCustomIngredient={setShowEditCustomIngredient}
          customIngredient={editingCustomIngredient}
          formState={formState}
          setFormState={setFormState}
          index={editingCustomIngredientIndex}
          onAllergenUpdate={onAllergenUpdate}
        />
        <AddRecipeIngredientModal
          visible={showAddRecipeModal}
          onCancel={() => setShowAddRecipeModal(false)}
          onAddRecipe={handleAddRecipeIngredient}
          availableRecipes={availableRecipes}
          currentRecipeId={formState._id}
        />
        <StyledCard>
          {/* Shared with missing ingredients — informational, not blocking */}
          {update &&
          recipe?.needsIngredientReview &&
          recipe?.missingSharedIngredients?.length > 0 ? (
            <div
              style={{
                background: '#fff2e8',
                border: '1px solid #ffb366',
                borderRadius: '6px',
                padding: '16px',
                marginBottom: '20px',
                color: '#d46b08',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <span style={{ fontSize: '16px', marginRight: '8px' }}>⚠</span>
                <strong>Needs Ingredient Review</strong>
              </div>
              <p style={{ margin: '0 0 6px', fontSize: 13 }}>
                This recipe was shared from another venue. Some ingredients
                could not be matched. Saving this recipe will clear the review
                warning.
              </p>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>
                Missing ingredients:
              </div>
              <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                {recipe.missingSharedIngredients.map((ing, idx) => (
                  <li key={idx}>
                    {ing.title || 'Unknown'}
                    {ing.sku ? ` (SKU: ${ing.sku})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* Missing Products Warning */}
          {hasMissingProducts && (
            <div
              style={{
                background: '#fff2e8',
                border: '1px solid #ffb366',
                borderRadius: '6px',
                padding: '16px',
                marginBottom: '20px',
                color: '#d46b08',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '8px',
                }}
              >
                <span style={{ fontSize: '16px', marginRight: '8px' }}>⚠️</span>
                <strong>Recipe has missing products</strong>
              </div>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                This recipe contains {missingProducts.length} ingredient(s) that
                are not in your shopping list. You cannot edit this recipe until
                all ingredients are available.
              </p>
              <div style={{ fontSize: '13px' }}>
                <strong>Missing ingredients:</strong>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                  {missingProducts.map((missing, idx) => (
                    <li key={idx}>
                      {missing.ingredient.title || 'Unknown ingredient'} -{' '}
                      {missing.reason}
                    </li>
                  ))}
                </ul>
                <div style={{ marginTop: '12px' }}>
                  <Button
                    type='primary'
                    size='small'
                    onClick={handleAddMissingProducts}
                    style={{
                      backgroundColor: '#d46b08',
                      borderColor: '#d46b08',
                      fontSize: '12px',
                    }}
                  >
                    Add Missing Products to Shopping List
                  </Button>
                </div>
              </div>
            </div>
          )}

          <Form onSubmit={handleSubmit} layout='vertical'>
            <Form.Item
              label='Recipe Name'
              onClick={() => setOpenCategoryDropdown(false)}
            >
              <Input
                value={formState.title}
                onChange={e => handleChange('title', e.target.value)}
                required
              />
            </Form.Item>
            {!isSlimFirstRecipe ? (
              <Form.Item label='Category'>
                <CustomCategorySearch
                  categories={categories}
                  handleChange={handleChange}
                  setOpen={setOpenCategoryDropdown}
                  open={openCategoryDropdown}
                  value={formState.category}
                />
              </Form.Item>
            ) : null}

            {!isSlimFirstRecipe ? (
              <Form.Item
                label='Description'
                onClick={() => setOpenCategoryDropdown(false)}
              >
                <TextArea
                  value={formState.description}
                  onChange={e => handleChange('description', e.target.value)}
                />
              </Form.Item>
            ) : null}
            {!isSlimFirstRecipe ? (
              <FileUpload onClick={() => setOpenCategoryDropdown(false)}>
                {localImageUrl || formState.image ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'start',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      position: 'relative',
                      width: '100%',
                      maxWidth: '500px',
                    }}
                  >
                    <img
                      width='100%'
                      height='auto'
                      src={
                        formState.image
                          ? formState.image.signedURL
                            ? formState.image.signedURL
                            : formState.image
                          : localImageUrl
                      }
                      alt='uploaded'
                      style={{
                        opacity: isUploading ? 0.5 : 1,
                        transition: 'opacity 0.3s ease-in-out',
                        borderRadius: '8px',
                      }}
                    />
                    {isUploading && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'rgba(0, 0, 0, 0.4)',
                          borderRadius: '8px',
                        }}
                      >
                        <Icon
                          type='loading'
                          style={{ fontSize: 32, color: 'white' }}
                        />
                      </div>
                    )}

                    <CloseOutlined
                      onClick={handleDeleteImage}
                      style={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        fontSize: '20px',
                        cursor: 'pointer',
                        color: 'white',
                        background: 'rgba(0,0,0,0.5)',
                        padding: '5px',
                        borderRadius: '50%',
                      }}
                    />
                  </div>
                ) : (
                  <Button
                    type='primary'
                    onClick={() => fileInputRef.current.click()}
                  >
                    Upload Recipe Image
                  </Button>
                )}
                <input
                  type='file'
                  accept='.jpg,.jpeg,.png,image/jpeg,image/jpg,image/png'
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </FileUpload>
            ) : null}

            <SectionDivider>
              <h3>OMS Ingredients</h3>
            </SectionDivider>

            {filteredProducts.length > 0 ? (
              <ShoppingListGuidanceBanner venueId={venueId} />
            ) : (
              <ShoppingListEmptyEducation venueId={venueId} />
            )}

            {formState.ingredients.map((ingredient, index) => (
              <IngredientRow
                key={index}
                onClick={() => setOpenCategoryDropdown(false)}
              >
                <Form.Item label='SKU'>
                  <SearchableSelect
                    placeholder='SKU'
                    value={ingredient.sku || ''}
                    onChange={value =>
                      handleIngredientChange(index, 'productId', value)
                    }
                    products={filteredProducts}
                    style={{ width: 150 }}
                    type='sku'
                  />
                </Form.Item>
                <Form.Item label='Title'>
                  <SearchableSelect
                    placeholder='Title'
                    value={ingredient.title || ''}
                    onChange={value =>
                      handleIngredientChange(index, 'productId', value)
                    }
                    products={filteredProducts}
                    style={{ width: 360 }}
                    type='title'
                  />
                </Form.Item>

                {ingredient?.conversions?.length > 0 && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Form.Item label='Conversion'>
                      {(() => {
                        const conversionOptions = (
                          ingredient.conversions || []
                        ).map((conv, i) => {
                          const conversionId =
                            conv?.conversionId || conv?._id || conv?.id || ''
                          const fallbackKey = [
                            conv?.label || '',
                            conv?.toQty ?? '',
                            conv?.toUnit || '',
                            i,
                          ].join('::')
                          return {
                            key: conversionId
                              ? `__conversion__${conversionId}`
                              : `__conversion__${fallbackKey}`,
                            conversion: conv,
                          }
                        })

                        const selectedConversionKey = (() => {
                          if (!ingredient?.conversion) return '__original__'
                          const selectedId =
                            ingredient?.conversion?.conversionId ||
                            ingredient?.conversion?._id ||
                            ingredient?.conversion?.id
                          if (selectedId) return `__conversion__${selectedId}`
                          const fallbackKey = [
                            ingredient?.conversion?.label || '',
                            ingredient?.conversion?.toQty ?? '',
                            ingredient?.conversion?.toUnit || '',
                          ].join('::')
                          const matched = conversionOptions.find(option =>
                            option.key.startsWith(
                              `__conversion__${fallbackKey}`,
                            ),
                          )
                          return matched?.key || '__original__'
                        })()

                        return (
                          <Select
                            placeholder='Select conversion'
                            style={{ width: 120 }}
                            value={selectedConversionKey}
                            onChange={selectedKey => {
                              const updatedIngredients = [
                                ...formState.ingredients,
                              ]
                              const extractSize = extractAndCalculateSize(
                                updatedIngredients[index].defaultSize,
                              )
                              if (selectedKey === '__original__') {
                                updatedIngredients[index] = {
                                  ...updatedIngredients[index],
                                  size: extractSize.numbers[0],
                                  UoM: ingredient.defaultUnit,
                                  conversion: null,
                                  totalPrice:
                                    calculateTotalPrice(
                                      ingredient.defaultSize,
                                      ingredient.price,
                                      extractSize.numbers[0],
                                    ) || '',
                                }
                              } else {
                                const selectedConversion =
                                  conversionOptions.find(
                                    option => option.key === selectedKey,
                                  )?.conversion
                                updatedIngredients[index] = {
                                  ...updatedIngredients[index],
                                  conversion: selectedConversion || null,
                                  size: selectedConversion?.toQty,
                                  UoM: selectedConversion?.toUnit,
                                  totalPrice:
                                    calculateTotalPrice(
                                      ingredient.defaultSize,
                                      ingredient.price,
                                      extractSize.numbers[0],
                                    ) || '',
                                }
                              }
                              setFormState(prev => ({
                                ...prev,
                                ingredients: updatedIngredients,
                              }))
                            }}
                          >
                            <Option value='__original__'>{`${
                              ingredient.defaultSize || 'Original'
                            } (Original)`}</Option>

                            {conversionOptions.map(({ key, conversion }, i) => (
                              <Option key={key || i} value={key}>
                                {`${conversion?.label} (${conversion?.toQty} ${
                                  conversion?.toUnit || ''
                                })`}
                              </Option>
                            ))}
                          </Select>
                        )
                      })()}
                    </Form.Item>
                  </div>
                )}
                {ingredient.conversion &&
                typeof ingredient.conversion === 'object' ? (
                  <div
                    style={{
                      display: 'flex',
                    }}
                  >
                    <Form.Item label='Size'>
                      <InputNumber
                        placeholder='Size'
                        min={0}
                        value={
                          ingredient.size ||
                          ingredient?.conversion?.toQty ||
                          undefined
                        }
                        onChange={value =>
                          handleIngredientChange(
                            index,
                            'conversionQuantity',
                            value,
                          )
                        }
                        style={{ width: 100 }}
                      />
                    </Form.Item>
                    {getConversionCategory(ingredient?.UoM) === 'count' ? (
                      <Form.Item label='UoM'>
                        <Input
                          placeholder='UoM'
                          value={ingredient.UoM || ''}
                          onChange={e =>
                            handleIngredientChange(index, 'UoM', e.target.value)
                          }
                          style={{ width: 100 }}
                          disabled
                        />
                      </Form.Item>
                    ) : (
                      <Form.Item label='UoM'>
                        <Select
                          value={
                            ingredient?.UoM ||
                            ingredient?.conversion?.toUnit ||
                            ''
                          }
                          onChange={value =>
                            handleIngredientChange(
                              index,
                              'conversionUnit',
                              value,
                            )
                          }
                          style={{ width: 130 }}
                        >
                          {(
                            conversionOptions[
                              getConversionCategory(
                                ingredient?.conversion?.toUnit,
                              )
                            ] || []
                          ).map(option => (
                            <Option key={option.value} value={option.value}>
                              {option.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Form.Item label='Size'>
                      <InputNumber
                        placeholder='Size'
                        min={0}
                        value={ingredient.size || undefined}
                        onChange={value =>
                          handleIngredientChange(index, 'size', value)
                        }
                        style={{ width: 100 }}
                      />
                    </Form.Item>
                    <Form.Item label='UoM'>
                      <Input
                        placeholder='UoM'
                        value={ingredient.UoM || ''}
                        onChange={e =>
                          handleIngredientChange(index, 'UoM', e.target.value)
                        }
                        style={{ width: 100 }}
                        disabled
                      />
                    </Form.Item>
                  </div>
                )}
                <Form.Item label='Price'>
                  <InputNumber
                    placeholder='Price'
                    min={0}
                    step={0.01}
                    value={ingredient.price || undefined}
                    onChange={value =>
                      handleIngredientChange(index, 'price', value)
                    }
                    formatter={value =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    style={{ width: 100 }}
                    disabled
                  />
                </Form.Item>
                <Form.Item label='Total Price'>
                  <InputNumber
                    placeholder='Total Price'
                    min={0}
                    step={0.01}
                    value={
                      Number(ingredient?.totalPrice)?.toFixed(3) || undefined
                    }
                    onChange={value =>
                      handleIngredientChange(index, 'totalPrice', value)
                    }
                    formatter={value =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    style={{ width: 100 }}
                    disabled
                  />
                </Form.Item>

                <Button
                  type='link'
                  icon='delete'
                  onClick={() => {
                    const newIngredients = formState.ingredients.filter(
                      (_, i) => i !== index,
                    )
                    setFormState(prev => ({
                      ...prev,
                      ingredients: newIngredients,
                    }))
                  }}
                  style={{ color: '#ff4d4f', marginBottom: 12 }}
                />
                <Button
                  type='link'
                  icon='edit'
                  onClick={() => {
                    setSelectedIngredient({
                      ...ingredient,
                      index,
                    })
                    setShowEditIngredient(true)
                  }}
                  style={{ color: '#ff4d4f', marginBottom: 12 }}
                />
              </IngredientRow>
            ))}

            {formState.customProducts.length > 0 && (
              <SectionDivider>
                <h3>Custom Ingredients</h3>
              </SectionDivider>
            )}

            {formState.customProducts.map((item, index) => (
              <IngredientRow key={`custom-${index}`}>
                <Form.Item label='SKU' style={{ flex: 1 }}>
                  <Input
                    value={item?.product?.sku || item.sku}
                    onChange={e =>
                      handleChangeCustomProduct(
                        index,
                        'sku',
                        e.target.value,
                        true,
                      )
                    }
                  />
                </Form.Item>
                <Form.Item label='Title' style={{ flex: 1 }}>
                  <Input
                    value={item?.product?.title || item.title}
                    onChange={e =>
                      handleChangeCustomProduct(
                        index,
                        'title',
                        e.target.value,
                        item.product ? true : false,
                      )
                    }
                  />
                </Form.Item>
                <Form.Item label='Size' style={{ width: 120 }}>
                  <Input
                    type='number'
                    value={item.size}
                    onChange={e =>
                      handleChangeCustomProduct(
                        index,
                        'size',
                        Number(e.target.value),
                        item.product ? true : false,
                      )
                    }
                  />
                </Form.Item>
                <Form.Item label='Price' style={{ width: 120 }}>
                  <Input
                    type='number'
                    prefix='£'
                    step={0.01}
                    min={0}
                    value={getCustomProductPricePounds(item)}
                    onChange={e =>
                      handleChangeCustomProduct(
                        index,
                        'price',
                        Number(e.target.value),
                        item.product ? true : false,
                      )
                    }
                  />
                </Form.Item>
                <Form.Item label='UoM' style={{ width: 120 }}>
                  <Input
                    value={item?.product?.UoM || item.UoM}
                    onChange={e =>
                      handleChangeCustomProduct(
                        index,
                        'UoM',
                        e.target.value,
                        item.product ? true : false,
                      )
                    }
                  />
                </Form.Item>
                <Form.Item label='Total Price' style={{ width: 140 }}>
                  <Input
                    value={(
                      getCustomProductPricePounds(item) * item.size
                    )?.toFixed(2)}
                    prefix='£'
                    disabled
                  />
                </Form.Item>
                <Button
                  type='link'
                  icon='edit'
                  className='edit-button'
                  onClick={() => {
                    setShowEditCustomIngredient(true)
                    setEditingCustomIngredientIndex(index)
                    setEditingCustomIngredient(item)
                  }}
                  style={{ color: '#1890ff' }}
                />
                <Button
                  type='link'
                  icon='delete'
                  className='delete-button'
                  onClick={() => {
                    const indexToDelete = index
                    const updatedProducts = formState.customProducts.filter(
                      (_, index) => index !== indexToDelete,
                    )
                    setFormState(prev => ({
                      ...prev,
                      customProducts: updatedProducts,
                    }))
                  }}
                  style={{ color: '#ff4d4f' }}
                />
              </IngredientRow>
            ))}

            {/* Recipe Ingredients Section */}
            {formState.recipeIngredients.length > 0 && (
              <SectionDivider>
                <h3>Recipe Ingredients</h3>
              </SectionDivider>
            )}

            {formState.recipeIngredients.map(recipeIngredient => (
              <RecipeIngredientRow
                key={recipeIngredient.id}
                recipeIngredient={recipeIngredient}
                onUpdate={handleUpdateRecipeIngredient}
                onRemove={handleRemoveRecipeIngredient}
                onToggleLock={handleToggleRecipeLock}
                availableRecipes={availableRecipes}
              />
            ))}

            <Form.Item onClick={() => setOpenCategoryDropdown(false)}>
              <Button
                type='dashed'
                onClick={() =>
                  setFormState(prev => ({
                    ...prev,
                    ingredients: [
                      ...prev.ingredients,
                      {
                        sku: '',
                        title: '',
                        size: '',
                        price: '',
                        UoM: '',
                        totalPrice: '',
                      },
                    ],
                  }))
                }
                style={{ width: '30%' }}
              >
                <Icon type='plus' /> Add OMS Ingredient
              </Button>
              {!isSlimFirstRecipe ? (
                <>
                  <Button
                    onClick={() => setIsModalVisible(true)}
                    style={{ width: '30%', marginLeft: 10 }}
                  >
                    <Icon type='plus' /> Add Custom Ingredient
                  </Button>
                  <Button
                    onClick={() => setShowAddRecipeModal(true)}
                    style={{ width: '30%', marginLeft: 10 }}
                  >
                    <Icon type='plus' /> Add OMS Recipe
                  </Button>
                </>
              ) : null}
            </Form.Item>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 10,
                fontSize: 20,
              }}
            >
              <Text strong>Cost Per Recipe:</Text>
              <Text strong>{formatGbp(costPerRecipe)}</Text>
            </div>

            <div style={{ marginBottom: 32 }}>
              <Title style={{ marginBottom: 10, fontSize: 20 }}>Portions</Title>
              <InputNumber
                style={{ width: '100%', height: 40 }}
                parser={value => value.replace(/Â£\s?/, '')}
                value={formState.portions || 1}
                onChange={value => handleChange('portions', value)}
                required
                size='large'
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 10,
                fontSize: 20,
              }}
            >
              <Text strong>Cost Per Portion:</Text>
              <Text strong>
                {formatRecipeCostPerPortionLabel(costPerPortion)}
              </Text>
            </div>
            <div style={{ marginBottom: 24 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 10,
                }}
              >
                <Title
                  style={{ marginBottom: 0, marginRight: 12, fontSize: 20 }}
                >
                  Has VAT?
                </Title>
                <input
                  type='checkbox'
                  checked={formState.hasVat}
                  onChange={e => handleChange('hasVat', e.target.checked)}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                  }}
                />
                <span
                  style={{ marginLeft: 8, fontSize: '14px', color: '#666' }}
                >
                  Adds 20% VAT to your selling price.
                </span>
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <Title
                data-testid='recipe-price-selling-label'
                style={{ marginBottom: 10, fontSize: 20 }}
              >
                {RECIPE_SELLING_PRICE_LABEL}
              </Title>
              <PriceFieldHint>{RECIPE_SELLING_PRICE_HINT}</PriceFieldHint>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  padding: '4px 11px',
                  height: '32px',
                  transition: 'all 0.3s',
                }}
              >
                <span
                  style={{
                    marginRight: '8px',
                    fontWeight: 'bold',
                    fontSize: '18px',
                  }}
                >
                  £
                </span>
                <input
                  type='text'
                  inputMode='decimal'
                  pattern='[0-9]*'
                  aria-label={RECIPE_SELLING_PRICE_LABEL}
                  data-testid='recipe-price-selling-input'
                  placeholder='e.g. 8.50'
                  value={sellingPriceInclVat}
                  onFocus={() => {
                    setIsEditingSellingPrice(true)
                    setTempSellingPriceInclVat(sellingPriceInclVat)
                  }}
                  onChange={e => {
                    const value = e.target.value
                    if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
                      return
                    }
                    setTempSellingPriceInclVat(value)

                    if (value === '' || value === '.') {
                      if (value === '') {
                        setFormState(prev => ({
                          ...prev,
                          sellingPrice: '',
                          grossProfit: '',
                        }))
                      }
                      return
                    }

                    lastPricingEditRef.current = 'selling'
                    editingSourceRef.current = 'selling'
                    const marginPrice = calculateMarginPrice(
                      value,
                      formState.hasVat,
                    )
                    if (marginPrice) {
                      const gp = calculateGrossProfitPercent(
                        marginPrice,
                        costPerPortion,
                      )
                      setFormState(prev => ({
                        ...prev,
                        sellingPrice: marginPrice,
                        grossProfit: gp !== null ? gp : prev.grossProfit,
                      }))
                    }
                    editingSourceRef.current = null
                  }}
                  onBlur={e => {
                    const value = e.target.value
                    setIsEditingSellingPrice(false)

                    if (value && value !== '' && value !== '.') {
                      lastPricingEditRef.current = 'selling'
                      editingSourceRef.current = 'selling'
                      const marginPrice = calculateMarginPrice(
                        value,
                        formState.hasVat,
                      )
                      if (marginPrice) {
                        const gp = calculateGrossProfitPercent(
                          marginPrice,
                          costPerPortion,
                        )
                        setFormState(prev => ({
                          ...prev,
                          sellingPrice: marginPrice,
                          grossProfit: gp !== null ? gp : prev.grossProfit,
                        }))
                      }
                      editingSourceRef.current = null
                    } else if (value === '' || value === '.') {
                      setFormState(prev => ({
                        ...prev,
                        sellingPrice: '',
                        grossProfit: '',
                      }))
                    }

                    setTempSellingPriceInclVat('')
                  }}
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    height: '100%',
                    padding: 0,
                    fontSize: '18px',
                    fontWeight: 'bold',
                    backgroundColor: 'transparent',
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <Title
                data-testid='recipe-price-margin-label'
                style={{ marginBottom: 10, fontSize: 20 }}
              >
                {RECIPE_MARGIN_EX_VAT_LABEL}
              </Title>
              <PriceFieldHint>{RECIPE_MARGIN_EX_VAT_HINT}</PriceFieldHint>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  padding: '4px 11px',
                  height: '32px',
                  transition: 'all 0.3s',
                }}
              >
                <span
                  style={{
                    marginRight: '8px',
                    fontWeight: 'bold',
                    fontSize: '18px',
                  }}
                >
                  £
                </span>
                <input
                  type='text'
                  inputMode='decimal'
                  pattern='[0-9]*'
                  aria-label={RECIPE_MARGIN_EX_VAT_LABEL}
                  data-testid='recipe-price-margin-input'
                  value={
                    isEditingMargin
                      ? tempMarginDisplay
                      : formatMarginPriceForDisplay(sellingPrice)
                  }
                  onFocus={() => {
                    setIsEditingMargin(true)
                    setTempMarginDisplay(
                      formatMarginPriceForDisplay(sellingPrice) || '',
                    )
                  }}
                  onChange={e => {
                    const value = e.target.value
                    if (!/^\d*\.?\d*$/.test(value) || !validatePrice(value)) {
                      return
                    }
                    setTempMarginDisplay(value)
                    if (value === '' || value === '.') {
                      return
                    }
                    lastPricingEditRef.current = 'margin'
                    editingSourceRef.current = 'margin'
                    const stored = formatMarginPriceForStorage(value)
                    const gp = calculateGrossProfitPercent(
                      stored,
                      costPerPortion,
                    )
                    setFormState(prev => ({
                      ...prev,
                      sellingPrice: stored,
                      grossProfit: gp !== null ? gp : prev.grossProfit,
                    }))
                    editingSourceRef.current = null
                  }}
                  onBlur={() => {
                    setIsEditingMargin(false)
                    if (tempMarginDisplay && validatePrice(tempMarginDisplay)) {
                      updatePricingFromMargin(tempMarginDisplay, 'margin')
                    } else if (!tempMarginDisplay) {
                      setFormState(prev => ({
                        ...prev,
                        sellingPrice: '',
                        grossProfit: '',
                      }))
                    }
                    setTempMarginDisplay('')
                  }}
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    height: '100%',
                    padding: 0,
                    fontSize: '18px',
                    fontWeight: 'bold',
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <Title
                data-testid='recipe-price-gp-label'
                style={{ marginBottom: 10, fontSize: 20 }}
              >
                {RECIPE_GP_PERCENT_LABEL}
              </Title>
              <PriceFieldHint>{RECIPE_GP_PERCENT_HINT}</PriceFieldHint>
              <InputNumber
                style={{ width: '100%', height: 40 }}
                min={0}
                max={99.99}
                step={0.01}
                precision={2}
                aria-label={RECIPE_GP_PERCENT_LABEL}
                placeholder={RECIPE_GP_PERCENT_PLACEHOLDER}
                value={
                  grossProfit === '' || grossProfit === null
                    ? undefined
                    : grossProfit
                }
                formatter={value =>
                  value !== undefined && value !== '' ? `${value}%` : ''
                }
                parser={value => value.replace('%', '').trim()}
                onChange={handleGpChange}
                size='large'
              />
            </div>

            {!isSlimFirstRecipe ? (
              <>
                <RecipeNotes
                  handleChange={handleChange}
                  uploadingIndexes={uploadingIndexes}
                  setUploadingIndexes={setUploadingIndexes}
                  notes={formState.notes}
                  update={update}
                />
                <MethodSection
                  handleChange={handleChange}
                  steps={formState.steps}
                />

                <MenusAndSectionsPanel
                  recipe={formState}
                  onMenuAssignmentChange={() => {
                    // TODO: Handle menu assignment changes
                  }}
                />
              </>
            ) : null}

            <Form.Item
              style={{
                marginBottom: 0,
                marginTop: 24,
              }}
              onClick={() => setOpenCategoryDropdown(false)}
            >
              <Button
                disabled={saveDisabled}
                loading={isLoading}
                onClick={handleSubmit}
                type='primary'
              >
                Save Recipe
              </Button>
              {saveDisabledReason ? (
                <SaveDisabledReason data-testid='recipe-save-disabled-reason'>
                  {saveDisabledReason}
                </SaveDisabledReason>
              ) : null}
            </Form.Item>
          </Form>
        </StyledCard>
      </Modal>
    </div>
  )
}

export default RecipeManager
